import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import Logo from "@/components/Logo";
import LangSwitcher from "@/components/LangSwitcher";
import UniversalSearch from "@/components/UniversalSearch";
import { Linkedin, Youtube, Facebook, Instagram, LayoutDashboard, LogOut, ChevronDown, Menu, X } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import NewsletterSubscribe from "@/components/NewsletterSubscribe";
import Seo from "@/components/Seo";

export default function PublicLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Simple nav items (top level)
  const navItems = [
    { to: "/news", label: t("nav_news") },
    { to: "/events", label: t("nav_events") },
    { to: "/projects", label: t("nav_projects") },
    { to: "/formations", label: t("nav_formations") },
    { to: "/soutenez-nous", label: t("nav_support_us") },
  ];

  // Sprint 75 — Grouped "La Communauté" sub-menu
  const communityItems = [
    { to: "/members", label: t("nav_members") || "Membres" },
    { to: "/opportunites", label: `${t("nav_opportunities")} 🚀` },
    { to: "/about", label: t("nav_about") || "À propos" },
    { to: "/contact", label: t("nav_contact") },
  ];
  const [openCommunity, setOpenCommunity] = useState(false);
  const dropdownRef = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpenCommunity(false); };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  useEffect(() => { setOpenCommunity(false); }, [location.pathname]);
  const isCommunityActive = communityItems.some((c) => location.pathname.startsWith(c.to));

  return (
    <div className="min-h-screen bg-white">
      {/* Default SEO — ensures canonical/og:url always points to www.lawinetech.com */}
      <Seo path={location.pathname} />

      {/* Top Nav */}
      <header className="sticky top-0 z-40 border-b border-neutral-100 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:gap-6 md:px-6 md:py-4">
          <Logo />
          <nav className="hidden items-center gap-7 lg:flex">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${isActive ? "text-wine-600" : "text-neutral-700 hover:text-wine-600"}`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}

            {/* Sprint 75 — La Communauté dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setOpenCommunity((v) => !v)}
                className={`inline-flex items-center gap-1 text-sm font-medium transition-colors ${isCommunityActive ? "text-wine-600" : "text-neutral-700 hover:text-wine-600"}`}
                data-testid="nav-community-toggle"
                aria-expanded={openCommunity}
                aria-haspopup="true"
              >
                {t("nav_community")} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openCommunity ? "rotate-180" : ""}`} />
              </button>
              {openCommunity && (
                <div className="absolute right-0 mt-2 w-52 overflow-hidden rounded-2xl border border-neutral-100 bg-white shadow-xl ring-1 ring-black/5" data-testid="nav-community-dropdown">
                  {communityItems.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      className={({ isActive }) =>
                        `block px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-wine-50 text-wine-700" : "text-neutral-700 hover:bg-neutral-50 hover:text-wine-600"}`
                      }
                      data-testid={`nav-community-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>
          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden md:block"><UniversalSearch /></div>
            <LangSwitcher />
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-wine-600 hover:shadow-md" data-testid="nav-to-admin">
                    <LayoutDashboard className="h-4 w-4" /> {t("portal_admin")}
                  </Link>
                )}
                {user.role === "journalist" && (
                  <Link to="/press" className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-wine-600 hover:shadow-md" data-testid="nav-to-press">
                    <LayoutDashboard className="h-4 w-4" /> {t("portal_press")}
                  </Link>
                )}
                {(user.role === "member" || user.role === "applicant" || user.role === "event_user") && (
                  <Link to="/portal" className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-wine-600 hover:shadow-md" data-testid="nav-to-portal">
                    <LayoutDashboard className="h-4 w-4" /> {t("portal_member")}
                  </Link>
                )}
                <button
                  onClick={() => logout().then(() => nav("/"))}
                  className="hidden md:inline-flex rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-all hover:bg-neutral-50"
                  data-testid="nav-logout"
                  aria-label={t("logout")}
                  title={t("logout")}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="hidden md:inline text-sm font-medium text-neutral-700 hover:text-wine-600" data-testid="nav-login">
                  {t("login")}
                </Link>
                <Link
                  to="/join"
                  className="hidden md:inline-flex rounded-full bg-wine-500 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-wine-600 hover:shadow-md"
                  data-testid="nav-join-cta"
                  title={t("layout_publiclayout_90")}
                >
                  {t("cta_join")}
                </Link>
              </>
            )}
            {/* Mobile hamburger toggle — only visible below lg breakpoint */}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center rounded-full border border-neutral-200 bg-white p-2 text-neutral-700 hover:bg-neutral-50"
              data-testid="mobile-menu-toggle"
              aria-expanded={mobileOpen}
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-neutral-100 bg-white" data-testid="mobile-menu-drawer">
            <div className="max-h-[80vh] overflow-y-auto px-4 py-3 space-y-1">
              <div className="pb-2"><UniversalSearch /></div>
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) => `block rounded-lg px-3 py-2.5 text-sm font-semibold ${isActive ? "bg-wine-50 text-wine-700" : "text-neutral-700 hover:bg-neutral-50"}`}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="pt-1 pb-1 text-[10px] font-bold uppercase tracking-wider text-neutral-400">{t("nav_community")}</div>
              {communityItems.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  className={({ isActive }) => `block rounded-lg px-3 py-2 text-sm ${isActive ? "bg-wine-50 text-wine-700 font-semibold" : "text-neutral-600 hover:bg-neutral-50"}`}
                  data-testid={`mobile-nav-community-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {c.label}
                </NavLink>
              ))}
              <hr className="my-3" />
              {user ? (
                <>
                  {user.role === "admin" && <Link to="/admin" className="block rounded-lg bg-wine-500 px-3 py-2.5 text-sm font-bold text-white text-center" data-testid="mobile-nav-to-admin"><LayoutDashboard className="mr-1.5 inline h-4 w-4" />{t("portal_admin")}</Link>}
                  {user.role === "journalist" && <Link to="/press" className="block rounded-lg bg-wine-500 px-3 py-2.5 text-sm font-bold text-white text-center"><LayoutDashboard className="mr-1.5 inline h-4 w-4" />{t("portal_press")}</Link>}
                  {(user.role === "member" || user.role === "applicant" || user.role === "event_user") && <Link to="/portal" className="block rounded-lg bg-wine-500 px-3 py-2.5 text-sm font-bold text-white text-center" data-testid="mobile-nav-to-portal"><LayoutDashboard className="mr-1.5 inline h-4 w-4" />{t("portal_member")}</Link>}
                  <button onClick={() => logout().then(() => nav("/"))} className="mt-2 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
                    <LogOut className="mr-1.5 inline h-4 w-4" />{t("logout")}
                  </button>
                </>
              ) : (
                <div className="space-y-2">
                  <Link to="/login" className="block rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-sm font-semibold text-neutral-700 text-center hover:bg-neutral-50" data-testid="mobile-nav-login">{t("login")}</Link>
                  <Link to="/join" className="block rounded-lg bg-wine-500 px-3 py-2.5 text-sm font-bold text-white text-center" data-testid="mobile-nav-join">{t("cta_join")}</Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      {/* Sticky floating Newsletter button (Sprint 75) — visible on every public page */}
      <FloatingNewsletterButton />

      {/* Footer */}
      <footer className="mt-24 border-t border-neutral-100 bg-wine-50/30">
        {/* Newsletter band (Sprint 75) */}
        <div className="border-b border-wine-100 bg-gradient-to-r from-wine-50 via-amber-50 to-wine-50">
          <div className="mx-auto flex max-w-7xl flex-col items-start gap-4 px-6 py-8 md:flex-row md:items-center md:justify-between">
            <div className="md:max-w-xl">
              <h3 className="font-serif text-xl text-navy md:text-2xl">📧 {t("newsletter_band_title")}</h3>
              <p className="mt-1 text-sm text-neutral-600">{t("newsletter_band_desc")}</p>
            </div>
            <NewsletterSubscribe variant="inline" source="public_footer" className="w-full md:w-auto md:min-w-[420px]" />
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="grid gap-10 md:grid-cols-4">
            <div className="md:col-span-2">
              <Logo subtitle={t("innovate_together").toUpperCase()} linkTo={null} />
              <p className="mt-4 max-w-md text-sm text-neutral-600">
                {t("footer_intro")}
              </p>
              <div className="mt-4 flex gap-3">
                <a href="https://www.youtube.com/@lawinetech" target="_blank" rel="noreferrer" className="text-neutral-500 hover:text-wine-600" data-testid="social-youtube"><Youtube className="h-5 w-5" /></a>
                <a href="https://www.linkedin.com/company/la-winetech" target="_blank" rel="noreferrer" className="text-neutral-500 hover:text-wine-600" data-testid="social-linkedin"><Linkedin className="h-5 w-5" /></a>
                <a href="https://www.facebook.com/lawinetech" target="_blank" rel="noreferrer" className="text-neutral-500 hover:text-wine-600" data-testid="social-facebook"><Facebook className="h-5 w-5" /></a>
                <a href="https://www.instagram.com/lawinetech/" target="_blank" rel="noreferrer" className="text-neutral-500 hover:text-wine-600" data-testid="social-instagram"><Instagram className="h-5 w-5" /></a>
              </div>
            </div>
            <div>
              <h4 className="font-serif text-base text-neutral-900">{t("footer_platform")}</h4>
              <ul className="mt-3 space-y-2 text-sm text-neutral-600">
                <li><Link to="/members" className="hover:text-wine-600">{t("nav_members")}</Link></li>
                <li><Link to="/events" className="hover:text-wine-600">{t("nav_events")}</Link></li>
                <li><Link to="/news" className="hover:text-wine-600">{t("nav_news")}</Link></li>
                <li><Link to="/join" className="hover:text-wine-600" data-testid="footer-join">{t("cta_join")}</Link></li>
                <li><Link to="/apply" className="inline-flex items-center gap-1 font-semibold text-wine-600 hover:text-wine-700" data-testid="footer-apply">{t("layout_publiclayout_01")}</Link></li>
                <li><Link to="/contact" className="hover:text-wine-600">{t("nav_contact")}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-serif text-base text-neutral-900">{t("footer_spaces")}</h4>
              <ul className="mt-3 space-y-2 text-sm text-neutral-600">
                <li><Link to="/portal" className="hover:text-wine-600">{t("portal_member")}</Link></li>
                <li><Link to="/press" className="hover:text-wine-600">{t("portal_press")}</Link></li>
                <li><Link to="/login" className="hover:text-wine-600">{t("login")}</Link></li>
                <li><Link to="/about" className="hover:text-wine-600">{t("nav_about")}</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-neutral-200 pt-6 text-xs text-neutral-500 md:flex-row">
            <span>© {new Date().getFullYear()} La WineTech. {t("footer_rights")}</span>
            <span>{t("footer_made_with")}</span>
          </div>
        </div>
      </footer>

      {/* EtOH credit banner */}
      <div className="bg-navy text-neutral-200" data-testid="etoh-credit-banner">
        <div className="mx-auto max-w-7xl px-6 py-6 text-center text-sm leading-relaxed">
          <p className="font-semibold text-white">
            {t("credit_dev_by")}{" "}
            <a
              href="https://etoh.io"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-wine-400 underline-offset-4 transition-colors hover:text-wine-200"
              data-testid="etoh-site-link"
            >
              EtOH
            </a>{" "}
            {t("credit_for")}
          </p>
          <p className="mt-2 text-neutral-300">{t("layout_publiclayout_02")}</p>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <a
              href="https://etoh.io"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 font-semibold text-navy transition-colors hover:bg-neutral-100"
              data-testid="etoh-site-cta"
            >
              🌐 etoh.io
            </a>
            <a
              href="mailto:contact@etoh.io"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 font-semibold text-white transition-colors hover:bg-white/20"
              data-testid="etoh-contact-link"
            >
              ✉️ contact@etoh.io
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

// Sprint 75 — Floating "Subscribe to newsletter" CTA visible on every public page.
function FloatingNewsletterButton() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label={t("newsletter_subscribe_aria")}
        className="fixed bottom-6 left-6 z-30 inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-3 text-sm font-bold text-white shadow-2xl transition-all hover:bg-wine-600 hover:scale-105 hover:shadow-wine-200 print:hidden"
        data-testid="floating-newsletter-btn"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
        Newsletter
      </button>
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-3xl bg-white p-2 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="floating-newsletter-modal">
            <NewsletterSubscribe variant="card" source="floating_cta" />
            <button onClick={() => setOpen(false)} className="mt-3 w-full rounded-full border border-neutral-200 px-4 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-50" data-testid="floating-newsletter-close">{t("newsletter_later")}</button>
          </div>
        </div>
      )}
    </>
  );
}
